import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useApiRoutes } from '@/lib/utils/useApiRoutes'
import type {
  BrandingAssetKey,
  BrandingAssetState,
  BrandingState,
} from '@/views/configs/brandingAssets'

/**
 * Tenant-branding store — single source of truth for the live
 * branding state across the portal. Pulls from `GET /api/branding`
 * on first access (cached for the session); ConfigsBrandingSection
 * writes through `applyState(...)` after each mutation so consumers
 * (Header logo, future "company name" affordances) re-render
 * immediately without a re-fetch.
 *
 * ## Why a store (not per-component fetch)
 *
 *   - Header.vue + ConfigsBrandingSection both need the same state.
 *     Without a shared store the section's PATCH/upload responses
 *     wouldn't propagate to the header until a page reload.
 *   - Header mounts on app boot; section mounts on-demand. A store
 *     lets the header read the cached state immediately and the
 *     section avoid a duplicate fetch when navigating in.
 *
 * ## Lifecycle
 *
 *   - `ensureLoaded()` — idempotent fetch (returns the existing
 *     state if already loaded; otherwise fires `GET /api/branding`
 *     and caches the result). Header calls this on mount.
 *   - `refresh()` — force-refetch from the BE. Useful after a
 *     tenant impersonation switch or a session role change.
 *   - `applyState(next)` — REPLACE the cached state. Called by
 *     ConfigsBrandingSection after every PATCH / asset upload /
 *     asset reset response (the BE returns the full canonical
 *     state, so the FE just swaps the cache).
 */

export const useBrandingStore = defineStore('branding', () => {
  const routes = useApiRoutes()

  /** Last-known state from the BE. `null` until first load completes. */
  const state = ref<BrandingState | null>(null)
  /** True while a fetch is in flight. */
  const loading = ref(false)
  /** Memoizes the in-flight fetch so concurrent callers share one request. */
  let inflight: Promise<BrandingState | null> | null = null

  const companyName = computed<string | null>(() => state.value?.company_name ?? null)

  /**
   * Per-asset getter. Returns the live state slot (or `null` when
   * the store hasn't loaded yet — caller decides whether to wait
   * or render a fallback).
   */
  const asset = (key: BrandingAssetKey): BrandingAssetState | null =>
    state.value?.assets?.[key] ?? null

  const fetchOnce = async (): Promise<BrandingState | null> => {
    loading.value = true
    try {
      const resp = await routes.BRANDING.get<BrandingState>()
        .notifyOnError(false)
        .send()
      state.value = resp ?? null
      // Sync the browser-tab favicon to the tenant's app_icon (the
      // uploaded URL OR the canonical default). Once per app load,
      // not live — operators who upload a new icon mid-session see
      // the new tab favicon on next refresh. The `<link rel="icon">`
      // tags in `index.html` are the seed; this overwrites them in
      // place so the browser picks up the change without a full
      // document reload.
      const appIconUrl = state.value?.assets?.app_icon?.url
      if (appIconUrl) {
        syncBrowserFavicon(appIconUrl)
      }
      return state.value
    } catch {
      state.value = null
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Idempotent — returns the cached state if already loaded;
   * otherwise fetches once. Concurrent callers (e.g. Header mount +
   * Configs section open in quick succession) share one network
   * request via the `inflight` memo.
   */
  const ensureLoaded = (): Promise<BrandingState | null> => {
    if (state.value) return Promise.resolve(state.value)
    if (inflight) return inflight
    inflight = fetchOnce().finally(() => {
      inflight = null
    })
    return inflight
  }

  /** Force a fresh fetch — bypasses the cache. */
  const refresh = (): Promise<BrandingState | null> => {
    inflight = fetchOnce().finally(() => {
      inflight = null
    })
    return inflight
  }

  /**
   * REPLACE the cached state with a BE-supplied canonical snapshot.
   * Called by ConfigsBrandingSection after PATCH / upload / delete
   * responses — the BE returns the full state so consumers don't
   * need to merge.
   */
  const applyState = (next: BrandingState | null): void => {
    state.value = next
  }

  return {
    state,
    loading,
    companyName,
    asset,
    ensureLoaded,
    refresh,
    applyState,
  }
})

/**
 * Update every `<link rel="icon">` in the document head to point at
 * the supplied URL. Called from `fetchOnce` after the branding state
 * loads so the browser-tab favicon reflects the tenant's APP_ICON
 * (uploaded OR shipped default). One-shot per app load — the
 * operator-spec is "does not need to reflect live, can be the next
 * load," so we don't re-run this on upload responses.
 *
 * Why update ALL `link[rel="icon"]` (not just one): `index.html`
 * ships two entries (webp + png fallback). Setting both to the
 * resolved URL keeps the browser from racing to fetch the stale
 * fallback. The `type` attribute stays as the original (browsers
 * are forgiving on mismatched type vs actual extension; the
 * dynamic href wins).
 *
 * Top-level (NOT inside the store closure) because it's a pure
 * DOM utility — no Pinia state, no reactivity required.
 */
function syncBrowserFavicon(url: string): void {
  if (typeof document === 'undefined') return // SSR guard
  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
  links.forEach((link) => {
    link.href = url
  })
}
